import React, { useEffect, useState } from "react";
import { message, Modal } from "antd";

import { COLORS } from "../styled";
import { cognitoGetSession } from "../../services/cognito-auth";
import { shareWithFriendRequest, shareWithFriendByProfileIdRequest } from "../../axios/routes/user";
import {
  ShareModalSection,
  ShareModalLabel,
  ShareModalDescription,
  ShareUrlRow,
  ShareUrlText,
  ShareCopyButton,
  ShareDivider,
  SharedWithList,
  SharedWithBadge,
  ShareEmailRow,
  ShareEmailInput,
  ShareRemoveButton,
  ShareAddButton,
} from "../../pages/profile/styled";

export type ShareWithFriendsModalProps = {
  open: boolean;
  onClose: () => void;
  /** Current game id (required when open). */
  gameId: string | null;
  /** Link fans can copy (e.g. team subdomain + seat/game query). */
  gameUrl: string;
  accentColor?: string;
  /** Customer profile id for unauthenticated share API fallback. */
  profileId?: string | null;
  /** Emails already invited for this game (for display). */
  initialSharedEmails?: string[];
  /** After a successful send, parent can merge `sharedWith` into profile state. */
  onShared?: (sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }>) => void;
};

/**
 * Invite friends by email (same backend as Profile) plus optional copyable game URL.
 */
export function ShareWithFriendsModal({
  open,
  onClose,
  gameId,
  gameUrl,
  accentColor = COLORS.BLUE_MAIN,
  profileId,
  initialSharedEmails = [],
  onShared,
}: ShareWithFriendsModalProps): JSX.Element {
  const [friendEmails, setFriendEmails] = useState<string[]>([""]);
  const [shareSending, setShareSending] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setFriendEmails([""]);
      setUrlCopied(false);
    }
  }, [open, gameId]);

  const updateFriendEmail = (index: number, value: string) => {
    setFriendEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
  };

  const addFriendEmailRow = () => {
    setFriendEmails((prev) => [...prev, ""]);
  };

  const removeFriendEmailRow = (index: number) => {
    setFriendEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const validEmails = friendEmails
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (!gameId || !validEmails.length) {
      message.warning("Please enter at least one valid email address.");
      return;
    }
    setShareSending(true);
    try {
      let results: Array<{ sharedWith: Array<{ email: string; gameId: string; sharedAt: Date }> }>;
      try {
        const tokens = await cognitoGetSession();
        results = await Promise.all(
          validEmails.map((email) => shareWithFriendRequest(tokens.idToken, gameId, email)),
        );
      } catch (authErr: unknown) {
        const msg = authErr instanceof Error ? authErr.message : String(authErr);
        if (msg.includes("No authenticated user") && profileId) {
          results = await Promise.all(
            validEmails.map((e) => shareWithFriendByProfileIdRequest(profileId, gameId, e)),
          );
        } else {
          throw authErr;
        }
      }
      const latestSharedWith = results.length > 0
        ? results[results.length - 1].sharedWith
        : [];
      onShared?.(latestSharedWith);
      message.success(`Shared with ${validEmails.length} friend${validEmails.length > 1 ? "s" : ""}`);
      setFriendEmails([""]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to share. Please try again.";
      message.error(msg);
    } finally {
      setShareSending(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!gameUrl) return;
    try {
      await navigator.clipboard.writeText(gameUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      message.error("Unable to copy URL");
    }
  };

  return (
    <Modal
      title="Tag or Share with Friends"
      visible={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={420}
    >
      <ShareModalDescription>
        Share the Momento experience with your seat mates! Enter their email to invite them, or copy the game link to share.
      </ShareModalDescription>

      <ShareModalSection>
        <ShareModalLabel>Invite friends by email</ShareModalLabel>
        {friendEmails.map((email, idx) => (
          <ShareEmailRow key={`email-row-${email || idx}`}>
            <ShareEmailInput
              type="email"
              placeholder="friend@email.com"
              value={email}
              onChange={(e) => updateFriendEmail(idx, e.target.value)}
            />
            {friendEmails.length > 1 && (
              <ShareRemoveButton
                type="button"
                onClick={() => removeFriendEmailRow(idx)}
                title="Remove"
              >
                ×
              </ShareRemoveButton>
            )}
          </ShareEmailRow>
        ))}
        <ShareAddButton type="button" $teamColor={accentColor} onClick={addFriendEmailRow}>
          + Add another
        </ShareAddButton>
        <div style={{ marginTop: 12 }}>
          <ShareCopyButton
            $teamColor={accentColor}
            onClick={handleSend}
            disabled={shareSending || !friendEmails.some((e) => e.trim())}
            style={{ width: "100%" }}
          >
            {shareSending ? "Sending..." : `Send${friendEmails.filter((e) => e.trim()).length > 1 ? ` (${friendEmails.filter((e) => e.trim()).length})` : ""}`}
          </ShareCopyButton>
        </div>
        {gameId && initialSharedEmails.length > 0 && (
          <SharedWithList style={{ marginTop: 12 }}>
            <ShareModalLabel style={{ width: "100%", marginBottom: 4 }}>Shared with</ShareModalLabel>
            {initialSharedEmails.map((sharedEmail) => (
              <SharedWithBadge key={sharedEmail}>{sharedEmail}</SharedWithBadge>
            ))}
          </SharedWithList>
        )}
      </ShareModalSection>

      {gameUrl ? (
        <>
          <ShareDivider>or</ShareDivider>
          <ShareModalSection>
            <ShareModalLabel>Share game link</ShareModalLabel>
            <ShareUrlRow>
              <ShareUrlText>{gameUrl}</ShareUrlText>
              <ShareCopyButton $teamColor={accentColor} onClick={handleCopyUrl}>
                {urlCopied ? "Copied!" : "Copy"}
              </ShareCopyButton>
            </ShareUrlRow>
          </ShareModalSection>
        </>
      ) : null}
    </Modal>
  );
}
